import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Container, Row, Col, HeroSlider, size } from '@everlywell/leaves';
import { useInView } from 'react-intersection-observer';
import 'intersection-observer';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { sanitize } from 'utils/domHelpers';
import { SubscriptionOptionType } from '../../../containers/PDP/HeroContainer';
import { generateKey } from 'utils/helpers';
import AnchorLink from 'components/AnchorLink';
import MeasuredItemsModal from 'components/MeasuredItemsModal';

import lightsLeft from '../../../images/themes/Winter2020/lights-left.svg';
import lightsRight from '../../../images/themes/Winter2020/lights-right.svg';
import { Theme, Themable } from 'components/Themable';

import PurchaseControls from './PurchaseControls';

import * as S from './styles';

export type PDPHeroProps = {
  name: string;
  price: string;
  memberPrice?: string;
  memberHeroCopy?: string;
  membershipVariantId: number;
  activeCredits?: number;
  descriptionHeader: string;
  description: string;
  averageRating: number;
  reviewCount: number;
  sliderImages: { image: string | React.ReactNode; imageAlt: string }[];
  collectionMethods: Array<{ iconUrl: string; name: string }>;
  measurements: Array<{ iconUrl: string; name: string }>;
  subscriptionOptions: SubscriptionOptionType[];
  onSubscriptionOptionChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onAddToCartClick: (e: React.FormEvent<HTMLButtonElement>) => void;
  onExpressPayClick: Function;
  onPurchaseTypeChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onQuantityPickerChange: (e: React.FormEvent<HTMLInputElement>) => void;
  purchaseTypeRadioButtons: Array<{ label: string; name: string }>;
  activeSubscriptionOption: SubscriptionOptionType;
  activePurchaseType: any;
  subscribeSelected: boolean;
  topCallout?: string;
  bottomCallout?: string;
  descriptionCallout?: string;
  modalTitle?: string;
  modalButtonText?: string;
  listOfMeasuredItems?: Array<{
    title: string;
    image: string;
    items: string[];
  }>;
  hideSubscription?: boolean;
  onFrequencyTriggerClick: Function;
  onSlide: Function;
  ctaSubheadline: string;
  quantity: number;
  isOutOfStock: boolean;
  consultAvailable?: boolean;
  consultAvailableText: string;
  consultIcon: string;
};

export const PDPHero = ({
  name,
  price,
  memberPrice,
  memberHeroCopy,
  membershipVariantId,
  activeCredits,
  descriptionHeader,
  description,
  sliderImages,
  collectionMethods,
  measurements,
  onAddToCartClick,
  onExpressPayClick,
  subscriptionOptions,
  onSubscriptionOptionChange,
  activeSubscriptionOption,
  purchaseTypeRadioButtons,
  onPurchaseTypeChange,
  activePurchaseType,
  onQuantityPickerChange,
  subscribeSelected,
  averageRating,
  reviewCount,
  topCallout,
  bottomCallout,
  descriptionCallout,
  modalButtonText,
  modalTitle,
  listOfMeasuredItems,
  hideSubscription,
  onFrequencyTriggerClick,
  onSlide,
  ctaSubheadline,
  quantity,
  isOutOfStock,
  //@ts-ignore
  consultAvailable,
  consultAvailableText,
  consultIcon,
}: PDPHeroProps) => {
  const [openMemberModal, setOpenMemberModal] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const formattedPrice = (+price).toFixed(0);

  const formattedSubscriptionPrice = useMemo(
    () => (+activeSubscriptionOption?.price).toFixed(0),
    [activeSubscriptionOption],
  );

  const [showSticky, setShowSticky] = useState(false);
  const { ref, inView, entry } = useInView({
    threshold: 0,
  });

  const handleMeasuresModalClick = () => {
    setOpen(true);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Hero Measures Modal Link',
      },
    });
  };
  const userHasMembershipForTest = !!memberPrice;
  const final = memberPrice?.replace(/\D/g, '') || price;
  const stripeTotal = subscribeSelected
    ? activeSubscriptionOption.price
    : final;

  useEffect(() => {
    if (entry) {
      if (!inView) {
        if (entry.boundingClientRect.bottom < 0) {
          setShowSticky(true && !isOutOfStock);
        }
      } else {
        setShowSticky(false);
      }
    }
  }, [entry, inView]);

  ewAnalytics.useAttachTrackingToLinks(heroRef);

  return (
    <S.HeroWrapper ref={heroRef}>
      {topCallout ? (
        <S.Callout data-testid="topCallout">
          <span
            dangerouslySetInnerHTML={{
              __html: sanitize(topCallout),
            }}
          />
        </S.Callout>
      ) : null}
      {showSticky ? (
        <S.StickyOrderBarContainer data-testid="stickyAddToCart">
          <S.StickyContent>
            <S.StickyProductName>{name}</S.StickyProductName>
            <PurchaseControls
              purchaseTypeRadioButtons={purchaseTypeRadioButtons}
              onPurchaseTypeChange={onPurchaseTypeChange}
              selectedPurchaseType={activePurchaseType}
              subscriptionOptions={subscriptionOptions}
              onQuantityPickerChange={onQuantityPickerChange}
              quantity={quantity}
              onSubscriptionOptionChange={onSubscriptionOptionChange}
              selectedSubscriptionOption={activeSubscriptionOption}
              onAddToCartClick={onAddToCartClick}
              activeCredits={activeCredits}
              openMemberModal={openMemberModal}
              setOpenMemberModal={setOpenMemberModal}
              membershipVariantId={membershipVariantId}
              onFrequencyTriggerClick={onFrequencyTriggerClick}
              isStickyAppearance
              hideSubscription={hideSubscription}
              onExpressPayClick={onExpressPayClick}
              stripeTotal={stripeTotal}
              ctaSubheadline={ctaSubheadline}
            />
          </S.StickyContent>
        </S.StickyOrderBarContainer>
      ) : null}
      <S.Section data-testid="pdpHero">
        <Container>
          <Row>
            <Col xs={12} lg={6}>
              <S.ProductDescriptionHeaderMobile>
                {descriptionHeader}
              </S.ProductDescriptionHeaderMobile>
              <S.SliderContainer>
                <HeroSlider slideData={sliderImages} onSlide={onSlide} />
              </S.SliderContainer>
            </Col>
            <Col xs={12} lg={6}>
              <S.Content>
                <S.ProductDescriptionHeader>
                  {descriptionHeader}
                </S.ProductDescriptionHeader>
                <S.InfoSection>
                  <S.ProductName data-testid="heroProductName">
                    {name}
                  </S.ProductName>
                  {reviewCount ? (
                    <AnchorLink offset={size.xl5} href="#reviewsSection">
                      <S.Stars
                        rating={averageRating}
                        numOfReviews={reviewCount}
                      />
                    </AnchorLink>
                  ) : null}
                  <S.DescriptionContent>
                    <S.ProductDescription>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sanitize(description),
                        }}
                      />
                    </S.ProductDescription>
                    {listOfMeasuredItems &&
                    listOfMeasuredItems.length &&
                    modalButtonText &&
                    modalTitle ? (
                      <>
                        <S.MeasuresWrapper>
                          <S.MeasuresText
                            iconUrl={measurements[0].iconUrl}
                            name={measurements[0].name}
                            isHTML
                            data-testid="heroMeasuresText"
                          />
                          <S.ModalButton onClick={handleMeasuresModalClick}>
                            {modalButtonText}
                          </S.ModalButton>
                        </S.MeasuresWrapper>
                        <MeasuredItemsModal
                          modalTitle={modalTitle}
                          listOfItems={listOfMeasuredItems}
                          open={open}
                          openHandler={setOpen}
                        />
                      </>
                    ) : (
                      <S.MeasuresWrapper>
                        <S.MeasuresText
                          iconUrl={measurements[0].iconUrl}
                          name={measurements[0].name}
                          isHTML
                          data-testid="heroMeasuresText"
                        />
                      </S.MeasuresWrapper>
                    )}
                    <S.CollectionMethods data-testid="heroCollectionMethods">
                      {[...collectionMethods].map(method => (
                        <S.CollectionMethod
                          key={generateKey()}
                          iconUrl={method.iconUrl}
                          name={method.name}
                        />
                      ))}
                    </S.CollectionMethods>
                    {consultAvailable ? (
                      <S.ConsultWrapper>
                        <S.ConsultText
                          iconUrl={consultIcon}
                          name={consultAvailableText}
                          isHTML
                          data-testid="heroConsultAvailable"
                        />
                      </S.ConsultWrapper>
                    ) : null}
                  </S.DescriptionContent>
                </S.InfoSection>
                <S.PriceSection
                  data-test-id="heroProductPrice"
                  showDiscount={subscribeSelected || memberPrice}
                >
                  <S.PriceAmount>
                    <S.ProductPrice>{`$${formattedPrice}`}</S.ProductPrice>
                    {subscribeSelected ? (
                      <S.SubscribePrice>{`$${formattedSubscriptionPrice}`}</S.SubscribePrice>
                    ) : memberPrice ? (
                      <S.SubscribePrice>{memberPrice}</S.SubscribePrice>
                    ) : null}
                  </S.PriceAmount>
                  {subscribeSelected ? (
                    <S.SavingsText>
                      {activeSubscriptionOption.savingsStatement}
                    </S.SavingsText>
                  ) : memberHeroCopy ? (
                    <S.SavingsText>{memberHeroCopy}</S.SavingsText>
                  ) : null}
                </S.PriceSection>
                {descriptionCallout && !userHasMembershipForTest ? (
                  <S.DescriptionCallout data-testid="descriptionCallout">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitize(descriptionCallout),
                      }}
                    />
                  </S.DescriptionCallout>
                ) : null}
                <S.PurchaseSection ref={ref}>
                  {isOutOfStock ? (
                    <S.ProductDescription data-testid="outOfStockMessage">
                      This test is currently unavailable.
                    </S.ProductDescription>
                  ) : (
                    <PurchaseControls
                      purchaseTypeRadioButtons={purchaseTypeRadioButtons}
                      onPurchaseTypeChange={onPurchaseTypeChange}
                      selectedPurchaseType={activePurchaseType}
                      subscriptionOptions={subscriptionOptions}
                      onQuantityPickerChange={onQuantityPickerChange}
                      quantity={quantity}
                      onSubscriptionOptionChange={onSubscriptionOptionChange}
                      selectedSubscriptionOption={activeSubscriptionOption}
                      onAddToCartClick={onAddToCartClick}
                      activeCredits={activeCredits}
                      openMemberModal={openMemberModal}
                      setOpenMemberModal={setOpenMemberModal}
                      membershipVariantId={membershipVariantId}
                      hideSubscription={hideSubscription}
                      onFrequencyTriggerClick={onFrequencyTriggerClick}
                      onExpressPayClick={onExpressPayClick}
                      stripeTotal={stripeTotal}
                      ctaSubheadline={ctaSubheadline}
                    />
                  )}
                </S.PurchaseSection>
              </S.Content>
            </Col>
          </Row>
        </Container>
        {bottomCallout ? (
          <Themable>
            <S.BottomCallout theme={Theme.Default} data-testid="bottomCallout">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(bottomCallout),
                }}
              />
            </S.BottomCallout>
            <S.BottomCallout
              theme={Theme.Winter2020}
              data-testid="bottomCallout"
            >
              <S.BottomCalloutImgL src={lightsLeft} />
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(bottomCallout),
                }}
              />
              <S.BottomCalloutImgR src={lightsRight} />
            </S.BottomCallout>
          </Themable>
        ) : null}
      </S.Section>
    </S.HeroWrapper>
  );
};

export default PDPHero;
